/**
 * User account state management
 */
import Vue from 'vue'
import { objectFill } from '../mixins/object-fill'
import cloneDeep from 'lodash/cloneDeep'

const accountModel = {
      id: null,
      user_id: null,
      intercom_user_hash: null,
      type: null,
      first_name: null,
      last_name: null,
      name: null,
      email: null,
      phone: null,
      dob: null,
      gender: null,
      business_name:null,
      gst:null,
      company_id: null,
      company_name: null,
      company_type: null,
      insurance_documents: {},
      abn: null,
      billing_address: {
          sub_premises_number: null,
          street_number: null,
          street_address: null,
          suburb: null,
          state: {
              slug: null
          },
          postal_code: null
      },
      job_title: null,
      briefs_array: [],
      archived_briefs_array: [],
      shortlists_array: [],
      archived_shortlists_array: [],
      stripe_account: {},
      stripe_connect_account: {},
      bank_account_on_boarded: null,
  },
  tokenModel = {
      access_token: null,
      expires_in: null,
      created_at: null
  }

export { accountModel, tokenModel }

export default {
    namespaced: true,
    state: {
        authenticating: null,
        authPriority: false,
        authMessage: null,
        token: cloneDeep(tokenModel),
        account: cloneDeep(accountModel),
        permissions: {},
        should_complete_profile: false,
        should_be_redirected_to_listing_form: false,
        should_be_redirected_to_find_your_space: false
    },
    getters: {
        account (state) {
            return (state.account && state.account.email) ? state.account : null
        },
        authenticationMode (state) {
            return state.authenticating
        },
        authPriority (state) {
            return state.authPriority
        },
        authMessage (state) {
            return state.authMessage
        },
        token (state) {
            return state.token
        },
        accessToken (state) {
            if (!state.token) {
                return null
            }
            return state.token.access_token
        },
        shouldCompleteProfile (state) {
            return state.should_complete_profile
        },
        shouldBeRedirectedToListingForm (state) {
            return state.should_be_redirected_to_listing_form
        },
        shouldBeRedirectedToFindYourSpace (state) {
            return state.should_be_redirected_to_find_your_space
        },
        hasType (state) {
            return (state.account && state.account.email) ? !!state.account.type : false
        },
        isHirer (state) {
            return (state.account && state.account.email) ? state.account.type === 'hirer' : false
        },
        isLister (state) {
            return (state.account && state.account.email) ? state.account.type === 'lister' : false
        },
        briefs (state) {
            return (state.account && state.account.email) ? state.account.briefs_array : []
        },
        archivedBriefs (state) {
            return (state.account && state.account.email && Array.isArray(state.account.archived_briefs_array))
              ? state.account.archived_briefs_array
              : []
        },
        shortlists (state) {
            return (state.account && state.account.email && Array.isArray(state.account.shortlists_array))
              ? state.account.shortlists_array
              : []
        },
        archivedShortlists (state) {
            return (state.account && state.account.email && Array.isArray(state.account.archived_shortlists_array))
              ? state.account.archived_shortlists_array
              : []
        },
        shortlistById: (state) => (id) => {
            if (state.account && state.account.email) {
                for (let shortlist of state.account.shortlists_array) {
                    if (shortlist.id === id) {
                        return shortlist
                    }
                }
            }

            return null
        },
        shortlistLatestUpdated (state) {
            if (state.account && state.account.email && Array.isArray(state.account.shortlists_array)) {
                let shortlists_array = [...state.account.shortlists_array]
                return shortlists_array.sort((a, b) => {
                    if(new Date(a.updated_at) > new Date(b.updated_at)) {
                        return -1;
                    } else if (new Date(a.updated_at) < new Date(b.updated_at)){
                        return 1;
                    }
                    return 0;
                }).slice(0, 3)
            } else {
                return [];
            }
        },
        insuranceDocuments (state) {
            return (state.account && state.account.insurance_documents) ? state.account.insurance_documents : []
        },
        stripeCard(state){
            if (state.account.type !== 'hirer') {
                return true
            }
            return !!(
                state.account
                && state.account.stripe_account
                && state.account.stripe_account.stripe_customer_id
                && state.account.stripe_account.stripe_default_source
              )
        },
        defaultStripeCard (state) {
            return (
              state.account
              && state.account.stripe_account
              && state.account.stripe_account.stripe_default_source
            )
              ? state.account.stripe_account.stripe_default_source
              : null
        },
        stripeConnectAccount (state) {
            return (state.account && state.account.stripe_connect_account && state.account.stripe_connect_account.id)
              ? state.account.stripe_connect_account
              : null
        },
        stripeConnectIsOnboarded (state) {
            if (state.account.type !== 'lister') {
                return true
            }
            return !!(
              state.account
              && state.account.stripe_account
              && state.account.stripe_account.stripe_connect_onboarded
            )
        },
        bankAccountOnboarded (state) {
            if (state.account.type !== 'lister') {
                return true
            }
            return state.bank_account_on_boarded
        },
    },
    mutations: {
        token (state, token) {
            state.token = token
            state.token.created_at = Date.now()
        },
        authenticated (state, payload) {
            state.account = objectFill(cloneDeep(accountModel), payload)
            state.authMessage = null
        },
        updateAccount (state, payload) {
            state.account = objectFill(cloneDeep(accountModel), payload)
        },
        changeAuthMode (state, which) {
            if (typeof which === 'object' && which !== null) {
                state.authenticating = which.which
                state.authPriority = (which.priority) ? which.priority : false
            } else {
                state.authenticating = which
                state.authPriority = false
            }
        },
        deAuthenticated (state, message) {
            state.token = cloneDeep(tokenModel)
            state.account = cloneDeep(accountModel)
            state.authMessage = (message) ? message : null
            state.should_complete_profile = false
        },
        dismissAuth (state) {
            state.authenticating = null
        },
        dismissAuthMessage (state) {
            state.authMessage = null
        },
        setShouldCompleteProfile (state, should) {
            state.should_complete_profile = should
        },
        setShouldBeRedirectedToListingForm (state, should) {
            state.should_be_redirected_to_listing_form = should
        },
        setShouldBeRedirectedToFindYourSpace (state, should) {
            state.should_be_redirected_to_find_your_space = should
        },
        updateBriefs (state, payload) {
            state.account.briefs_array = payload
        },
        updateShortlists (state, payload) {
            state.account.shortlists_array = payload
        },
        setStripeDefaultCard (state, payload) {
            state.account.stripe_account.stripe_default_source = payload
        },
        setStripeConnectAccount (state, payload) {
            state.account.stripe_connect_account = payload
        },
        setBankAccountOnBoarded (state, payload) {
            state.bank_account_on_boarded = payload
        },
        setPhone (state, payload) {
            state.account.phone = payload
        },
        archiveShortlist(state, id) {
            const archivedShortlist = state.account.shortlists_array.filter(obj => obj.id == id)[0];
            state.account.shortlists_array = state.account.shortlists_array.filter(obj => obj.id != id);
            state.account.archived_shortlists_array.push(archivedShortlist);
        },
        restoreShortlist (state, id) {
            const unarchivedShortlist = state.account.archived_shortlists_array.filter(obj => obj.id == id)[0];
            state.account.archived_shortlists_array = state.account.archived_shortlists_array.filter(obj => obj.id != id);
            state.account.shortlists_array.push(unarchivedShortlist);
        },
        archiveBrief(state, id) {
            const archivedBrief = state.account.briefs_array.filter(obj => obj.id == id)[0];
            state.account.briefs_array = state.account.briefs_array.filter(obj => obj.id != id);
            state.account.archived_briefs_array.push(archivedBrief);
        },
        restoreBrief (state, id) {
            const unarchivedBrief = state.account.archived_briefs_array.filter(obj => obj.id == id)[0];
            state.account.archived_briefs_array = state.account.archived_briefs_array.filter(obj => obj.id != id);
            state.account.briefs_array.push(unarchivedBrief);
        }
    },
    actions: {
        logout (context) {
            return new Promise((resolve, reject) => {
                axios.post('/api/auth/logout')
                  .then((response) => {
                      resolve(response)
                  })
                  .catch((error) => {
                      reject(error)
                  })
                  .then(() => {
                      context.commit('deAuthenticated')
                      //context.dispatch('silentRefreshTokenTimer', true)
                  })

                  context.dispatch('intercom/shutdown', {}, {root:true})
            })
        },
        refreshUser (context) {
            axios.get('/api/auth/login').then(response => {
                  context.commit('authenticated', response.data)
              })
              .catch(() => {
                  context.commit('deAuthenticated', 'Your session has expired, please login')
              })

            if (context.getters.isLister) {
                axios.get("/api/account/bank-account-details").then(response => {
                    let bankAccountOnboarded = !!response.data.bank_account_name && !!response.data.bank_account_bsb && !!response.data.bank_account_number;
                    context.commit('setBankAccountOnBoarded', bankAccountOnboarded);
                  })
            }
        },

        /**
         * See if we're currently logged in
         */
        requireAuthentication (context) {
            return new Promise((resolve, reject) => {
                axios.get('/api/auth/login')
                  .then(response => {
                      // Refresh token

                      context.commit('authenticated', response.data)
                      resolve(response.data.user)
                  })
                  .catch(error => {
                      context.commit('deAuthenticated', 'Your session has expired, please login')
                      reject(error)
                  })
            })
        },

        toggleShortlistProperties (context, payload) {
            return new Promise((resolve, reject) => {
                let shortlist = context.getters['shortlistById'](payload.shortlistId)
                let enable = (typeof payload.shortlisted !== 'undefined') ? payload.shortlisted : true

                if (shortlist) {
                    let shortlistUrl;
                    if(enable) {
                        shortlistUrl = `/api/account/shortlists/${shortlist.id}/listings/add`;
                    } else {
                        shortlistUrl = `/api/account/shortlists/${shortlist.id}/listings/remove`;
                    }
                    axios.post(shortlistUrl, { listings: payload.listings })
                        .then((response) => {
                            context.state.account.shortlists_array = response.data
                            resolve()
                        }).catch(() => {
                            reject()
                        });
                } else {
                    reject()
                }
            })
        },

        addPaymentMethod (context, payload) {
            return new Promise((resolve, reject) => {
                axios.post('/api/account/payment-methods', payload)
                  .then(response => {
                      Vue.prototype.$toasted.show('Your card has been saved.', {
                          type: 'success'
                      })
                      resolve(response)
                  })
                  .catch(error => {
                      if (typeof error.response !== 'undefined' && error.response.status === 422) {
                          return Vue.prototype.$toasted.show(
                            'Please check your card details and try again.',
                            { type: 'error' }
                          )
                      }
                      Vue.prototype.$toasted.show(
                        'Your card could not be added at this time, please try again later.',
                        { type: 'error' }
                      )
                      reject(error)
                  })
                  .then(() => {
                      resolve()
                  })
            })
        }
    }
}
